import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchUsers, fetchRoles, createUser, updateUser, deleteUser } from '../../api';
import { useAccess } from '../auth/AccessContext';
import DataGrid from '../common/DataGrid';
import Modal from '../marketing/Modal';
import { actionsColumn, formatDate, toISODate } from '../marketing/shared';
import '../reports/Reports.css';
import '../marketing/Marketing.css';

// Who may use the app. A user is added here by email — normally before that
// person has ever signed in: Firebase only proves an email belongs to whoever
// is logging in, and this table decides whether that email gets in at all and
// what it may see. Someone who signs in without a row here is refused, so
// adding them ahead of time is what "prepping" an account means.

// Shows how far along someone's onboarding is, which is the main thing you want
// from this list after adding a batch of people.
const signInStatus = (user) => {
    if (!user.active) return 'Deactivated';
    return user.first_login_at ? 'Signed in' : 'Invited — not signed in yet';
};

export default function Users() {
    const { me } = useAccess();
    const [users, setUsers] = useState([]);
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // The open dialog: { mode: 'create' | 'edit' | 'delete', user }.
    const [dialog, setDialog] = useState(null);
    const [form, setForm] = useState({ email: '', name: '', roleId: '', active: true });
    const [saving, setSaving] = useState(false);
    const [dialogError, setDialogError] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const [userList, roleList] = await Promise.all([fetchUsers(), fetchRoles()]);
            setUsers(userList || []);
            setRoles(roleList || []);
        } catch (err) {
            setError(err.message);
            setUsers([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(); }, [load]);

    const openCreate = () => {
        // Default to the least-privileged profile so a mis-click can't hand out
        // more access than intended.
        const fallback = [...roles].reverse().find(r => !r.is_admin) || roles[0];
        setForm({ email: '', name: '', roleId: fallback ? String(fallback.id) : '', active: true });
        setDialogError(null);
        setDialog({ mode: 'create' });
    };

    const openEdit = useCallback((user) => {
        setForm({
            email: user.email,
            name: user.name || '',
            roleId: user.role_id ? String(user.role_id) : '',
            active: user.active,
        });
        setDialogError(null);
        setDialog({ mode: 'edit', user });
    }, []);

    const openDelete = useCallback((user) => {
        setDialogError(null);
        setDialog({ mode: 'delete', user });
    }, []);

    const closeDialog = () => { setDialog(null); setDialogError(null); };

    const handleSubmit = async () => {
        setSaving(true);
        setDialogError(null);
        try {
            const payload = {
                email: form.email.trim(),
                name: form.name.trim() || null,
                role_id: form.roleId ? Number(form.roleId) : null,
                active: form.active,
            };
            if (dialog.mode === 'create') await createUser(payload);
            else if (dialog.mode === 'edit') await updateUser(dialog.user.id, payload);
            else await deleteUser(dialog.user.id);
            setDialog(null);
            await load();
        } catch (err) {
            setDialogError(err.message);
        } finally {
            setSaving(false);
        }
    };

    const columnDefs = useMemo(() => [
        { field: 'email', headerName: 'Email', flex: 1, minWidth: 220 },
        { field: 'name', headerName: 'Name', width: 180 },
        { field: 'role_name', headerName: 'Profile', width: 150 },
        {
            colId: 'status',
            headerName: 'Status',
            width: 200,
            valueGetter: (p) => (p.data ? signInStatus(p.data) : ''),
        },
        { field: 'last_login_at', headerName: 'Last Sign-In', width: 140, valueFormatter: (p) => formatDate(p.value) },
        { field: 'invited_by', headerName: 'Added By', width: 200 },
        { field: 'created_at', headerName: 'Added', width: 130, valueFormatter: (p) => formatDate(p.value) },
        actionsColumn(openEdit, openDelete),
    ], [openEdit, openDelete]);

    const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true, minWidth: 120 }), []);

    // Editing your own row is fine, but the server refuses to let you
    // deactivate or delete it — say so before the request rather than after.
    const editingSelf = dialog?.user && me && dialog.user.email.toLowerCase() === me.email.toLowerCase();

    return (
        <div className="page">
            <div className="mkt__header">
                <div>
                    <h1 className="page__title">Users</h1>
                    <p className="page__subtitle">
                        Everyone who may sign in. Add people by email before their first login and assign
                        a profile — an email that isn't listed here can't get in, even on an allowed domain.
                    </p>
                </div>
                <div className="mkt__header-actions">
                    <button className="blueButton" onClick={openCreate} disabled={loading || roles.length === 0}>
                        + Add User
                    </button>
                </div>
            </div>

            {error && <p className="mkt__error" style={{ marginTop: 16 }}>{error}</p>}

            {loading && <p className="page__subtitle">Loading users…</p>}

            {!loading && !error && users.length === 0 && (
                <p className="page__subtitle" style={{ marginTop: 16 }}>
                    No users yet. Add the first one to let them in.
                </p>
            )}

            {users.length > 0 && (
                <div style={{ marginTop: 16 }}>
                    <DataGrid
                        rowData={users}
                        columnDefs={columnDefs}
                        defaultColDef={defaultColDef}
                        height="calc(100vh - 260px)"
                        enableExport
                        exportFileName={() => `Users_${toISODate(new Date())}`}
                    />
                </div>
            )}

            {dialog && dialog.mode !== 'delete' && (
                <Modal
                    title={dialog.mode === 'create' ? 'Add User' : 'Edit User'}
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel={dialog.mode === 'create' ? 'Add User' : 'Save Changes'}
                    submitting={saving}
                    submitDisabled={!form.email.trim() || !form.roleId}
                    error={dialogError}
                >
                    <label className="mkt__field">
                        <span>Email</span>
                        <input
                            type="email"
                            value={form.email}
                            onChange={(e) => setForm(f => ({ ...f, email: e.target.value }))}
                            placeholder="name@taxtitansusa.com"
                            autoFocus
                        />
                    </label>

                    <label className="mkt__field">
                        <span>Name (optional)</span>
                        <input
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm(f => ({ ...f, name: e.target.value }))}
                            placeholder="Jane Doe"
                        />
                    </label>

                    <label className="mkt__field">
                        <span>Profile</span>
                        <select
                            value={form.roleId}
                            onChange={(e) => setForm(f => ({ ...f, roleId: e.target.value }))}
                        >
                            <option value="">Select a profile…</option>
                            {roles.map(role => (
                                <option key={role.id} value={role.id}>{role.name}</option>
                            ))}
                        </select>
                    </label>

                    <label className="mkt__check">
                        <input
                            type="checkbox"
                            checked={form.active}
                            onChange={(e) => setForm(f => ({ ...f, active: e.target.checked }))}
                            disabled={editingSelf}
                        />
                        Active — can sign in
                    </label>
                    {editingSelf && (
                        <p className="mkt-modal__text">You can't deactivate your own account.</p>
                    )}

                    {dialog.mode === 'create' && (
                        <p className="mkt-modal__text">
                            They can sign in as soon as they create their Firebase login with this exact
                            email — no further step needed here.
                        </p>
                    )}
                </Modal>
            )}

            {dialog && dialog.mode === 'delete' && (
                <Modal
                    title="Remove User"
                    onClose={closeDialog}
                    onSubmit={handleSubmit}
                    submitLabel="Remove User"
                    submitVariant="danger"
                    submitting={saving}
                    submitDisabled={editingSelf}
                    error={dialogError}
                >
                    <p className="mkt-modal__text">
                        Remove <strong>{dialog.user.email}</strong>? They'll be locked out of the app
                        immediately, even though their sign-in still exists.
                    </p>
                    <p className="mkt-modal__text">
                        To keep their record — and their sign-in history — untick “Active” on the edit
                        form instead.
                    </p>
                    {editingSelf && (
                        <p className="mkt__error">You can't remove your own account.</p>
                    )}
                </Modal>
            )}
        </div>
    );
}
